import { Box, Typography } from '@mui/material';
import { EventIcon } from './EventIcon';
import { formatEventTime, formatEventDate } from '../../utils/dateGrouping';
import type { BaseEventTimelineItem } from '../../types/eventTimeline';

export interface EventRowProps {
  event: BaseEventTimelineItem;
}

/**
 * Single event row in the timeline
 * Displays event icon, description, details, notes, actor, and timestamp
 */
export function EventRow({ event }: EventRowProps) {
  return (
    <Box
      data-testid="timeline-event"
      sx={{
        display: 'flex',
        gap: 2,
        py: 2,
        px: 2,
        borderRadius: 1,
        transition: 'background-color 0.2s',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    >
      {/* Event Icon */}
      <Box sx={{ flexShrink: 0, pt: 0.5 }}>
        <EventIcon eventType={event.eventType} size="medium" />
      </Box>

      {/* Event Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Description */}
        <Typography
          variant="body2"
          data-testid="timeline-event-description"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 0.5,
          }}
        >
          {event.description}
        </Typography>

        {/* Details */}
        {event.details.length > 0 && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'block',
              mb: 0.5,
            }}
          >
            {event.details.join(' • ')}
          </Typography>
        )}

        {/* Notes */}
        {event.notes && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontStyle: 'italic',
              display: 'block',
              mb: 0.5,
            }}
          >
            Note: {event.notes}
          </Typography>
        )}

        {/* Actor and Timestamp */}
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
            display: 'block',
          }}
        >
          {event.actorUserName} • {formatEventTime(event.timestamp)} on{' '}
          {formatEventDate(event.timestamp)}
        </Typography>
      </Box>
    </Box>
  );
}
